import React, { useState, useEffect } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';

function CronJobs() {
    const [jobs, setJobs] = useState([]);

    // Form state for creating a new CRON job
    const [newJob, setNewJob] = useState({
        name: '',
        description: '',
        schedule: '',
        route_path: ''
    });

    // Fetch all cron jobs from the backend
    const fetchJobs = async () => {
        const response = await axios.get('https://api.eyorsolutions.com/cron-jobs');
        setJobs(response.data);
    };

    // Start the cron job
    const startJob = async (id) => {
        // Optimistically update the UI
        const updatedJobs = jobs.map((job) =>
            job.id === id ? { ...job, is_active: true } : job
        );
        setJobs(updatedJobs); // Update jobs in state immediately

        try {
            await axios.post(`https://api.eyorsolutions.com/cron-jobs/${id}/start`);
            // After successful backend call, do nothing (state already updated)
        } catch (error) {
            // Revert the change if there's an error in the backend
            const revertedJobs = jobs.map((job) =>
                job.id === id ? { ...job, is_active: false } : job
            );
            setJobs(revertedJobs);
            alert('Failed to start the job.');
        }
    };

    // Stop the cron job
    const stopJob = async (id) => {
        // Optimistically update the UI
        const updatedJobs = jobs.map((job) =>
            job.id === id ? { ...job, is_active: false } : job
        );
        setJobs(updatedJobs); // Update jobs in state immediately

        try {
            await axios.post(`https://api.eyorsolutions.com/cron-jobs/${id}/stop`);
            // After successful backend call, do nothing (state already updated)
        } catch (error) {
            // Revert the change if there's an error in the backend
            const revertedJobs = jobs.map((job) =>
                job.id === id ? { ...job, is_active: true } : job
            );
            setJobs(revertedJobs);
            alert('Failed to stop the job.');
        }
    };

    // Delete the cron job
    const deleteJob = async (id) => {
        await axios.delete(`https://api.eyorsolutions.com/cron-jobs/${id}`);
        fetchJobs();
    };

   // Download logs as CSV
    const downloadLogs = async (id) => {
        try {
            const response = await axios.get(`https://api.eyorsolutions.com/cron-logs/${id}/csv`, {
                responseType: 'blob', // Ensure we receive the data as a Blob
            });

            // Create a download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `cron_logs_job_${id}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading logs:', error);
            alert('Failed to download logs.');
        }
    };

    // Create a new cron job
    const createJob = async () => {
        if (!newJob.name || !newJob.schedule || !newJob.route_path) {
            alert('Please fill out all required fields!');
            return;
        }

        await axios.post('https://api.eyorsolutions.com/cron-jobs', newJob);
        setNewJob({
            name: '',
            description: '',
            schedule: '',
            route_path: ''
        });
        fetchJobs();
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    return (
        <div className="container mt-4">
            <h1>CRON Job Manager</h1>

            {/* Form to Create New CRON Job */}
            <div className="mb-4">
                <h2>Create New CRON Job</h2>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        createJob();
                    }}
                >
                    <div className="mb-3">
                        <label className="form-label">Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={newJob.name}
                            onChange={(e) => setNewJob({ ...newJob, name: e.target.value })}
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea
                            className="form-control"
                            value={newJob.description}
                            onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
                        ></textarea>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Schedule (CRON Expression)</label>
                        <input
                            type="text"
                            className="form-control"
                            value={newJob.schedule}
                            onChange={(e) => setNewJob({ ...newJob, schedule: e.target.value })}
                            placeholder="e.g., * * * * *"
                            required
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">API Route Path</label>
                        <input
                            type="text"
                            className="form-control"
                            value={newJob.route_path}
                            onChange={(e) => setNewJob({ ...newJob, route_path: e.target.value })}
                            placeholder="e.g., https://api.eyorsolutions.com/test"
                            required
                        />
                    </div>
                    <button type="submit" className="btn btn-primary">
                        Create CRON Job
                    </button>
                </form>
            </div>

            {/* List of Existing CRON Jobs */}
            <table className="table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Schedule</th>
                        <th>Path</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {jobs.map((job) => (
                        <tr key={job.id}>
                            <td>{job.name}</td>
                            <td>{job.schedule}</td>
                            <td>{job.route_path}</td>
                            <td>
                                {/* Start and Stop buttons */}
                                <button
                                    className="btn btn-success me-2"
                                    onClick={() => startJob(job.id)}
                                    disabled={job.is_active}
                                >
                                    Start
                                </button>
                                <button
                                    className="btn btn-danger me-2"
                                    onClick={() => stopJob(job.id)}
                                    disabled={!job.is_active}
                                >
                                    Stop
                                </button>
                                <button className="btn btn-warning me-2" onClick={() => deleteJob(job.id)}>
                                    Delete
                                </button>
                                <button className="btn btn-info" onClick={() => downloadLogs(job.id)}>
                                    Download Logs
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default CronJobs;
